import React, { useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '../../ui/Button'
import { formatEpcBySegments } from '../../../utils/epcComposer'
import type { RfidEpcScheme, RfidMatrixRow } from '../../../types/rfid'
import type { EncodeClassification } from '../../../lib/services/rfidEncodeService'

interface OverwriteConfirmationProps {
  scheme: RfidEpcScheme
  classification: Extract<EncodeClassification, { outcome: 'blocked' }>
  variant: RfidMatrixRow
  nextEpcPreview: string
  isEncoding: boolean
  onCancel: () => void
  onConfirm: () => void
}

function describeVariant(variant: RfidMatrixRow): string {
  return [variant.itemName, variant.colorName, variant.sizeName].filter(Boolean).join(' · ')
}

/**
 * Per-tag confirmation. There is deliberately no session toggle and no
 * "don't ask again": the whole value of the prompt is that it appears every
 * single time a known tag is presented.
 */
export function OverwriteConfirmation({
  scheme,
  classification,
  variant,
  nextEpcPreview,
  isEncoding,
  onCancel,
  onConfirm,
}: OverwriteConfirmationProps) {
  const record = classification.existingRecord
  const isHardBlock = !classification.canOverwrite

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  const currentItem = record
    ? [record.product_name, record.color_name, record.size_name].filter(Boolean).join(' · ')
    : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rfid-overwrite-title"
    >
      <div className="w-full max-w-lg rounded-xl border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 shadow-xl">
        <div className="flex items-start gap-3 border-b border-gray-200 dark:border-gray-700 px-5 py-4">
          <AlertTriangle className="h-6 w-6 text-amber-500 shrink-0" />
          <div>
            <h3
              id="rfid-overwrite-title"
              className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide"
            >
              {classification.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {classification.detail}
            </p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
              Currently on this tag
            </div>
            <dl className="space-y-1">
              <div className="flex gap-3">
                <dt className="w-16 text-gray-500">EPC</dt>
                <dd className="font-mono break-all text-gray-900 dark:text-gray-100">
                  {formatEpcBySegments(scheme, classification.preRead.epc)}
                </dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-16 text-gray-500">TID</dt>
                <dd className="font-mono break-all text-gray-900 dark:text-gray-100">
                  {classification.preRead.tid}
                </dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-16 text-gray-500">Item</dt>
                <dd className="text-gray-900 dark:text-gray-100">
                  {currentItem || 'Could not be identified'}
                </dd>
              </div>
              {record?.encoded_at && (
                <div className="flex gap-3">
                  <dt className="w-16 text-gray-500">Encoded</dt>
                  <dd className="text-gray-900 dark:text-gray-100">
                    {new Date(record.encoded_at).toLocaleString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {!isHardBlock && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                About to write
              </div>
              <dl className="space-y-1">
                <div className="flex gap-3">
                  <dt className="w-16 text-gray-500">EPC</dt>
                  <dd className="font-mono break-all text-gray-900 dark:text-gray-100">
                    {nextEpcPreview}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 text-gray-500">Item</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{describeVariant(variant)}</dd>
                </div>
              </dl>
            </div>
          )}

          {isHardBlock ? (
            <p className="rounded-lg bg-gray-100 dark:bg-gray-800 px-3 py-2 text-gray-700 dark:text-gray-200">
              Remove this tag and place a new RFID tag on the encoder.
            </p>
          ) : (
            <p className="rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-amber-800 dark:text-amber-200">
              {record
                ? `Overwriting retires unit ${record.serial} permanently. That serial is never reused.`
                : 'Overwriting erases the EPC currently on this tag. It cannot be recovered.'}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700 px-5 py-4">
          <Button autoFocus variant="secondary" onClick={onCancel} disabled={isEncoding}>
            {isHardBlock ? 'Close' : 'Cancel'}
          </Button>
          {!isHardBlock && (
            <Button variant="danger" onClick={onConfirm} disabled={isEncoding}>
              {isEncoding ? 'Writing…' : 'Overwrite this tag'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
